import {useQueryClient} from '@tanstack/react-query';
import debounce from 'lodash/debounce';
import {useRouter} from 'next/router';
import {
  ChangeEvent,
  SyntheticEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import {t} from '../../common/getTranslations';
import {requestContext} from '../../common/requestContext';
import useTypedDispatch from '../../common/useTypedDispatch';
import useTypedSelector from '../../common/useTypedSelector';
import {getTrimmedTextEvent} from '../../common/util/getTrimmedTextEvent';
import {SEARCH_INPUT_MAX_LENGTH} from '../../domain/search/constant';
import {useBodyScrollInIos} from '../../domain/search/hook/useBodyScrollInIos';
import {useSearchInputKeyword} from '../../domain/search/hook/useSearchInputKeyword';
import {setOpenAutoComplete} from '../../domain/search/state/searchSlice';
import {createSearchKeywordInput} from '../../domain/search/util/createSearchKeywordInput';
import {getAutoSaveRecentKeyword} from '../../recentSearchKeyword/autoSaveRecentKeyword.storage';
import {addRecentKeyword} from '../../recentSearchKeyword/recentKeywordList.storage';
import {useInfiniteSearchKeywordQuery} from '../../wrapper/searchWrapper';
import {MobileSearchHeader} from '../organisms/MobileSearchHeader/MobileSearchHeader';

interface MobileLayoutSearchHeaderProps {
  onClickBack: (e: SyntheticEvent) => void;
}

export function MobileLayoutSearchHeader({
  onClickBack,
}: MobileLayoutSearchHeaderProps) {
  const router = useRouter();
  const dispatch = useTypedDispatch();
  const isOpenAutoComplete = useTypedSelector(
    state => state.search.openAutoComplete
  );
  const queryClient = useQueryClient();
  const {inputKeyword, setInputKeyword, debouncedOnChangeInputKeyword} =
    useSearchInputKeyword();
  const onChangeKeyword = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      let value = '';
      if (requestContext.requestInfo.display.isAndroid) {
        const ev = getTrimmedTextEvent(event, SEARCH_INPUT_MAX_LENGTH);
        value = ev.target.value;
      } else {
        value = event.target.value;
      }
      setInputKeyword(value);
      debouncedOnChangeInputKeyword(value);
    },
    [debouncedOnChangeInputKeyword, setInputKeyword]
  );
  const onSubmitKeyword = useCallback(async () => {
    if (!inputKeyword) {
      return;
    }
    getAutoSaveRecentKeyword() && addRecentKeyword(inputKeyword);
    debouncedOnChangeInputKeyword.cancel();
    dispatch(setOpenAutoComplete(false));
    if (router.query.keyword === inputKeyword) {
      await queryClient.resetQueries({
        queryKey: useInfiniteSearchKeywordQuery.getKey({
          input: createSearchKeywordInput({keyword: inputKeyword}),
        }),
      });
    }
    router.push({
      pathname: '/search/result',
      query: {keyword: inputKeyword},
    });
  }, [
    debouncedOnChangeInputKeyword,
    dispatch,
    inputKeyword,
    queryClient,
    router,
  ]);

  const onClickKeywordCancel = useCallback(() => {
    if (router.pathname === '/search') {
      setInputKeyword('');
    } else {
      setInputKeyword(String(router.query.keyword || ''));
    }
    dispatch(setOpenAutoComplete(false));
  }, [dispatch, router.pathname, router.query, setInputKeyword]);

  const onClickKeywordRemove = useCallback(() => {
    setInputKeyword('');
    dispatch(setOpenAutoComplete(false));
  }, [dispatch, setInputKeyword]);

  const handleClickBack = useCallback(
    (event: SyntheticEvent) => {
      setInputKeyword('');
      onClickBack(event);
    },
    [onClickBack, setInputKeyword]
  );

  const onClickSearchInput = useCallback(() => {
    if (inputKeyword) {
      dispatch(setOpenAutoComplete(true));
    }
  }, [dispatch, inputKeyword]);

  const [isInputFocused, setIsInputFocused] = useState(false);

  const changeIsInputFocused = useMemo(
    () =>
      debounce(
        (isInputFocused: boolean) => setIsInputFocused(isInputFocused),
        100
      ),
    []
  );

  const onFocusInput = useCallback(() => {
    changeIsInputFocused(true);
  }, [changeIsInputFocused]);

  const onBlurInput = useCallback(() => {
    changeIsInputFocused(false);
  }, [changeIsInputFocused]);

  const shouldShowCancelButton = isInputFocused || isOpenAutoComplete;

  useBodyScrollInIos({
    needBodyScroll: !isOpenAutoComplete,
  });

  return (
    <MobileSearchHeader
      onClickBack={handleClickBack}
      onClickCancelButton={onClickKeywordCancel}
      onClickClearSearchText={onClickKeywordRemove}
      searchText={inputKeyword}
      onChangeSearchText={onChangeKeyword}
      onSubmit={onSubmitKeyword}
      searchPlaceholder={t('search_before_input')}
      shouldShowCancelButton={shouldShowCancelButton}
      onClickSearchInput={onClickSearchInput}
      onFocusInput={onFocusInput}
      onBlurInput={onBlurInput}
      isMobile={requestContext.requestInfo.display.isMobile}
    />
  );
}
